import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { Upload, Button, Table, message, Spin, Space } from 'antd';
import { UploadOutlined, FilePdfOutlined, DownloadOutlined, ClearOutlined } from '@ant-design/icons';
import '../styles/AutoDataPage.css';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const DEFAULT_PDF_NAME = 'ACTA-Alx.pdf';
const DEFAULT_PDF_URL = '/ACTA-Alx.pdf'; // 假设后端静态服务

// 后端 Node 代理根地址（3001）。生产构建时留空意味着与前端同源。
const API_BASE = process.env.REACT_APP_API_BASE || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '');

const AutoDataPage = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [fullData, setFullData] = useState([]); // 存储完整数据用于下载
    const [selectedFile, setSelectedFile] = useState(null); // File对象或字符串
    const [pdfUrl, setPdfUrl] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [logs, setLogs] = useState([]); // 存储实时日志
    const [currentStage, setCurrentStage] = useState(''); // 当前阶段
    const [, setRetryCount] = useState(0); // 重试次数
    const [currentSessionId, setCurrentSessionId] = useState(null); // 当前会话ID
    const eventSourceRef = useRef(null); // SSE连接引用
    const logsContainerRef = useRef(null); // 日志容器引用
    const connectionSuccessRef = useRef(false); // 连接成功标志
    const [userId, setUserId] = useState(null); // 浏览器指纹
    const [quota, setQuota] = useState(null);   // null 表示尚未加载
    const [paperSummary, setPaperSummary] = useState(''); // 论文总结
    const [paperMetadata, setPaperMetadata] = useState({}); // 论文元数据（标题、期刊等）
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    
    // 初始化 FingerprintJS
    useEffect(() => {
        const initFp = async () => {
            try {
                const cached = localStorage.getItem('matai_user_id');
                if (cached) {
                    setUserId(cached);
                    return;
                }
                const fp = await FingerprintJS.load();
                const res = await fp.get();
                localStorage.setItem('matai_user_id', res.visitorId);
                setUserId(res.visitorId);
            } catch (err) {
                console.error('Fingerprint init failed', err);
                const fallback = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                localStorage.setItem('matai_user_id', fallback);
                setUserId(fallback);
            }
        };
        initFp();
    }, []);

    // 获取配额
    useEffect(() => {
        if (!userId) return;
        const fetchQuota = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/extract/quota/${userId}`);
                const data = await res.json();
                setQuota(data.remaining_quota);
            } catch (err) {
                console.error('fetch quota err', err);
            }
        };
        fetchQuota();
    }, [userId]);

    // 自动滚动到日志底部
    useEffect(() => {
        if (logsContainerRef.current) {
            logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
        }
    }, [logs]);
    
    // 简化的日志监控
    useEffect(() => {
        console.log('Logs state changed, count:', logs.length);
        if (logs.length > 0) {
            console.log('Latest log:', logs[logs.length - 1]);
        }
    }, [logs]);
    
    const [columns] = useState([
        {
            title: 'Composition',
            dataIndex: 'composition',
            key: 'composition',
            width: 180,
            render: (text) => text ? text : <span className="not-extracted">Not extracted</span>,
        },
        {
            title: 'Brief Condition',
            dataIndex: 'brief_condition',
            key: 'brief_condition',
            width: 150,
            render: (text) => text ? text : <span className="not-extracted">Not extracted</span>,
        },
        {
            title: 'Ultimate Strength (MPa)',
            dataIndex: 'ultimate_strength',
            key: 'ultimate_strength',
            width: 120,
            render: (text) => text ? text : <span className="not-extracted">Not extracted</span>,
        },
        {
            title: 'Elongation (%)',
            dataIndex: 'elongation',
            key: 'elongation',
            width: 100,
            render: (text) => text ? text : <span className="not-extracted">Not extracted</span>,
        },
        {
            title: 'Density (g/cm³)',
            dataIndex: 'density',
            key: 'density',
            width: 100,
            render: (text) => text ? text : <span className="not-extracted">Not extracted</span>,
        },
        {
            title: 'Crystal Structure',
            dataIndex: 'crystal_structure',
            key: 'crystal_structure',
            width: 120,
            render: (text) => text ? text : <span className="not-extracted">Not extracted</span>,
        },
    ]);

    // 生成唯一ID
    const generateUniqueId = (record, index = 0) => {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 9);
        const composition = (record.composition || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
        const condition = (record.brief_condition || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
        return `${composition}-${condition}-${timestamp}-${random}-${index}`;
    };

    // 清理日志
    const clearLogs = () => {
        setLogs([]);
        setCurrentStage('');
        setRetryCount(0);
        setCurrentSessionId(null);
    };

    // 添加日志条目
    const addLog = (logEntry) => {
        const receiveTime = new Date().toLocaleTimeString() + '.' + new Date().getMilliseconds();
        console.log(`[${receiveTime}] Adding log:`, logEntry);
        
        const newLog = { 
            id: Date.now() + Math.random(), 
            ...logEntry,
            receiveTime: receiveTime
        };
        
        // 最简单的状态更新
        setLogs(prevLogs => {
            const updated = [...prevLogs, newLog];
            console.log(`[${receiveTime}] Logs updated, total count:`, updated.length);
            return updated;
        });
        
        setCurrentStage(logEntry.stage);
        
        console.log(`[${receiveTime}] State update completed`);
    };

    // 关闭SSE连接
    const closeEventSource = () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
    };

    // 取消当前任务
    const cancelCurrentTask = async () => {
        if (currentSessionId) {
            try {
                await fetch(`${API_BASE}/api/extract/cancel/${currentSessionId}`, {
                    method: 'POST'
                });
                console.log(`Cancelled task: ${currentSessionId}`);
            } catch (error) {
                console.error('Failed to cancel task:', error);
            }
        }
    };

    // 处理SSE消息
    const handleSSEMessage = (event) => {
        try {
            const data = JSON.parse(event.data);
            console.log('SSE message received:', data); // 添加调试信息
            
            // 检查是否包含session_id，如果是则设置当前会话
            if (data.data && data.data.session_id && !currentSessionId) {
                const sessionId = data.data.session_id;
                setCurrentSessionId(sessionId);
                console.log('Set session ID from SSE message:', sessionId);
            }
            
            // 如果这是第一条数据且还没显示连接成功，则显示连接成功
            if (!connectionSuccessRef.current && data.stage && data.message && !data.type) {
                connectionSuccessRef.current = true;
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'init',
                    message: '✅ Connection established, starting to receive data'
                });
            }
            
            if (data.type === 'summary') {
                // 处理论文总结中间结果
                flushSync(() => {
                    setPaperSummary(data.summary || '');
                    setPaperMetadata(data.metadata || {});
                });
                console.log('Paper summary received:', data.summary?.slice(0, 100) + '...');
            } else if (data.type === 'result') {
                // 处理最终结果
                const dataWithIds = data.data.map((item, index) => ({
                    ...item,
                    id: generateUniqueId(item, index)
                }));
                
                // 更新论文元数据（如果有更完整的信息）
                if (data.data.length > 0 && data.data[0]) {
                    const firstRecord = data.data[0];
                    if (firstRecord.title && firstRecord.title !== "Processing...") {
                        setPaperMetadata(prev => ({
                            ...prev,
                            title: firstRecord.title || prev.title,
                            journal: firstRecord.journal || prev.journal,
                            publication_date: firstRecord.publication_date || prev.publication_date
                        }));
                    }
                }
                
                flushSync(() => {
                    setData(dataWithIds);
                    setFullData(dataWithIds);
                    setLoading(false);
                });
                closeEventSource();
                message.success('Data extracted successfully');
            } else if (data.type === 'error') {
                // 处理错误
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'error',
                    message: data.message
                });
                flushSync(() => {
                    setLoading(false);
                });
                closeEventSource();
                message.error('Failed to extract data');
            } else {
                // 处理日志消息 - 立即显示
                addLog(data);
            }
        } catch (error) {
            console.error('Error parsing SSE message:', error);
            addLog({
                timestamp: new Date().toLocaleTimeString(),
                stage: 'error',
                message: '❌ Error parsing server response'
            });
        }
    };

    // 取消健康检查，始终返回 true
    // const checkServerHealth = async () => true;

    // 开始流式提取
    const startStreamExtraction = async (url) => {
        console.log('Starting SSE connection to:', url); // 添加调试信息
        
        // 重置连接成功标志
        connectionSuccessRef.current = false;
        
        addLog({
            timestamp: new Date().toLocaleTimeString(),
            stage: 'init',
            message: '🔗 Establishing connection...'
        });

        // 使用代理路径，确保路径正确
        const proxyUrlRelative = url.startsWith('/api/extract/') ? url : url.replace('http://localhost:8002/api/extract/', '/api/extract/');
        const proxyUrl = `${API_BASE}${proxyUrlRelative}`;
        console.log('Using proxy URL:', proxyUrl);

        // 创建SSE连接
        const eventSource = new EventSource(proxyUrl);
        eventSourceRef.current = eventSource;
        
        // 监听响应头以获取session_id（注意：EventSource无法直接获取自定义头，这是替代方案）
        // 我们会在第一条消息中包含session_id
        eventSource.addEventListener('open', () => {
            console.log('EventSource connection opened');
        });
        
        // 监听连接状态
        let connectionTimeout = setTimeout(() => {
            if (!connectionSuccessRef.current) {
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'init',
                    message: '⏳ Connection is taking longer than expected, please wait...'
                });
            }
        }, 3000);  // 3秒警告
        
        // 中等超时处理
        let mediumTimeout = setTimeout(() => {
            if (!connectionSuccessRef.current) {
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'init',
                    message: '⏳ Still connecting... Server may be initializing AI models...'
                });
            }
        }, 10000);  // 10秒中等警告
        
        // 添加额外的超时处理
        let maxConnectionTimeout = setTimeout(() => {
            if (!connectionSuccessRef.current) {
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'error',
                    message: '❌ Connection timeout. Please check server status and try again.'
                });
                flushSync(() => {
                    setLoading(false);
                });
                closeEventSource();
                message.error('Connection timeout - server may be overloaded');
            }
        }, 45000);  // 45秒最大超时，给AI模型初始化更多时间
        
        eventSource.onopen = (event) => {
            console.log('SSE connection opened (onopen event):', event);
            clearTimeout(connectionTimeout);
            clearTimeout(mediumTimeout);
            clearTimeout(maxConnectionTimeout);
            // 不在这里显示连接成功，因为onopen可能延迟触发
            // 改为在收到第一条数据时显示
        };
        
        eventSource.onmessage = (event) => {
            clearTimeout(connectionTimeout);
            clearTimeout(mediumTimeout);
            clearTimeout(maxConnectionTimeout);
            handleSSEMessage(event);
        };
        
        eventSource.onerror = (error) => {
            console.error('SSE Error:', error);
            clearTimeout(connectionTimeout);
            clearTimeout(mediumTimeout);
            clearTimeout(maxConnectionTimeout);
            
            // 检查是否是连接关闭
            if (eventSource.readyState === EventSource.CLOSED) {
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'error',
                    message: '❌ Connection closed by server'
                });
            } else if (eventSource.readyState === EventSource.CONNECTING) {
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'error',
                    message: '❌ Connection failed - server may be starting up or overloaded'
                });
            } else {
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'error',
                    message: '❌ Connection interrupted, please check server status and try again'
                });
            }
            
            flushSync(() => {
                setLoading(false);
            });
            closeEventSource();
            message.error('Connection error - please try again in a moment');
        };
    };

    // 上传PDF后，先不提取，只显示文件名和预览
    const handleUpload = (file) => {
        // 检查文件大小限制（50MB）
        const maxSize = 50 * 1024 * 1024; // 50MB in bytes
        if (file.size > maxSize) {
            message.error(`文件太大，最大支持 50MB。当前文件大小：${(file.size / (1024 * 1024)).toFixed(1)}MB`);
            return false;
        }
        
        setSelectedFile(file);
        setPdfUrl(URL.createObjectURL(file));
        setIsDefault(false);
        setData([]);
        setFullData([]);
        setPaperSummary('');
        setPaperMetadata({});
        clearLogs();
        return false; // 阻止自动上传
    };

    // 加载默认示例
    const handleDefaultExample = () => {
        setSelectedFile(DEFAULT_PDF_NAME);
        setPdfUrl(DEFAULT_PDF_URL);
        setIsDefault(true);
        setData([]);
        setFullData([]);
        setPaperSummary('');
        setPaperMetadata({});
        clearLogs();
    };

    // 点击Extract后才分析（流式版本）
    const handleExtractStream = async () => {
        if (!selectedFile) return;
        if (!userId || quota === null) {
            message.warning('User ID 正在初始化，请稍后再试');
            return;
        }
        if (quota <= 0) {
            message.warning('You have used up your quota of 10 extractions.');
            return;
        }
        
        console.log('Starting extraction stream...'); // 调试日志
        
        // 重置连接成功标志
        connectionSuccessRef.current = false;
        
        // 如果有活跃会话，先取消它
        if (currentSessionId) {
            await cancelCurrentTask();
        }
        
        // 关闭可能存在的连接
        closeEventSource();
        
        // 使用flushSync确保状态立即更新
        flushSync(() => {
            setLogs([]);
            setCurrentStage('');
            setLoading(true);
            setData([]);
            setFullData([]);
            setPaperSummary('');
            setPaperMetadata({});
            setCurrentSessionId(null);
        });
        
        // 立即添加一条初始日志
        addLog({
            timestamp: new Date().toLocaleTimeString(),
            stage: 'init',
            message: '🔗 Starting to establish connection...'
        });
        
        // 前端预扣一次配额（确保数字合法）
        setQuota(prev => (typeof prev === 'number' ? prev - 1 : prev));

        // 提取开始后再刷新后端配额
        const refreshQuotaLater = () => {
            setTimeout(async () => {
                try {
                    const res = await fetch(`${API_BASE}/api/extract/quota/${userId}`);
                    const data = await res.json();
                    setQuota(data.remaining_quota);
                } catch {}
            }, 1000);
        };

        if (isDefault) {
            // 使用默认示例的EventSource
            console.log('Using default example with EventSource');
            startStreamExtraction(`/api/extract/stream/default?user_id=${userId}`);
            refreshQuotaLater();
        } else {
            // 对于上传的文件，需要先上传再开始流式提取
            try {
                console.log('Starting upload for custom file');
                const formData = new FormData();
                formData.append('file', selectedFile);
                formData.append('user_id', userId);
                
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'init',
                    message: '📤 Uploading file...'
                });
                
                // 添加超时处理
                const controller = new AbortController();
                const timeoutId = setTimeout(() => {
                    controller.abort();
                }, 60000); // 60秒超时
                
                const response = await fetch(`${API_BASE}/api/extract/stream`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'X-User-ID': userId
                    },
                    signal: controller.signal
                });
                
                clearTimeout(timeoutId);
                
                if (!response.ok) {
                    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
                }
                
                // 获取session_id从响应头
                const sessionId = response.headers.get('X-Session-ID');
                if (sessionId) {
                    setCurrentSessionId(sessionId);
                    console.log('Set session ID from response header:', sessionId);
                }
                
                // 标记连接成功（对于fetch方式）
                connectionSuccessRef.current = true;
                addLog({
                    timestamp: new Date().toLocaleTimeString(),
                    stage: 'init',
                    message: '✅ Connection established, starting to read data stream'
                });
                
                // 使用fetch直接处理流式响应
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                
                const readStream = async () => {
                    try {
                        while (true) {
                            const { value, done } = await reader.read();
                            if (done) break;
                            
                            buffer += decoder.decode(value, { stream: true });
                            const lines = buffer.split('\n');
                            buffer = lines.pop() || ''; // 保留不完整的行
                            
                            for (const line of lines) {
                                if (line.startsWith('data: ')) {
                                    const jsonStr = line.slice(6).trim();
                                    if (jsonStr) {
                                        try {
                                            const data = JSON.parse(jsonStr);
                                            console.log('Received data:', data); // 添加调试日志
                                            
                                                                            if (data.type === 'summary') {
                                    // 处理论文总结中间结果
                                    flushSync(() => {
                                        setPaperSummary(data.summary || '');
                                        setPaperMetadata(data.metadata || {});
                                    });
                                    console.log('Paper summary received:', data.summary?.slice(0, 100) + '...');
                                } else if (data.type === 'result') {
                                    const dataWithIds = data.data.map((item, index) => ({
                                        ...item,
                                        id: generateUniqueId(item, index)
                                    }));
                                    
                                    // 更新论文元数据（如果有更完整的信息）
                                    if (data.data.length > 0 && data.data[0]) {
                                        const firstRecord = data.data[0];
                                        if (firstRecord.title && firstRecord.title !== "Processing...") {
                                            setPaperMetadata(prev => ({
                                                ...prev,
                                                title: firstRecord.title || prev.title,
                                                journal: firstRecord.journal || prev.journal,
                                                publication_date: firstRecord.publication_date || prev.publication_date
                                            }));
                                        }
                                    }
                                    
                                    flushSync(() => {
                                        setData(dataWithIds);
                                        setFullData(dataWithIds);
                                        setLoading(false);
                                    });
                                    message.success('Data extracted successfully');
                                    return;
                                            } else if (data.type === 'error') {
                                                addLog({
                                                    timestamp: new Date().toLocaleTimeString(),
                                                    stage: 'error',
                                                    message: data.message
                                                });
                                                flushSync(() => {
                                                    setLoading(false);
                                                });
                                                message.error('Failed to extract data');
                                                return;
                                            } else {
                                                // 处理日志消息 - 立即显示
                                                addLog(data);
                                            }
                                        } catch (parseError) {
                                            console.error('Error parsing JSON:', parseError, 'Raw data:', jsonStr);
                                            addLog({
                                                timestamp: new Date().toLocaleTimeString(),
                                                stage: 'error',
                                                message: '❌ Error parsing server response'
                                            });
                                        }
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        if (error.name === 'AbortError') {
                            addLog({
                                timestamp: new Date().toLocaleTimeString(),
                                stage: 'error',
                                message: '❌ Request timed out'
                            });
                        } else {
                            console.error('Stream reading error:', error);
                            addLog({
                                timestamp: new Date().toLocaleTimeString(),
                                stage: 'error',
                                message: `❌ Stream reading error: ${error.message}`
                            });
                        }
                        flushSync(() => {
                            setLoading(false);
                        });
                        message.error('Stream reading error occurred');
                    }
                };
                
                readStream();
                
                refreshQuotaLater();
                
            } catch (error) {
                if (error.name === 'AbortError') {
                    addLog({
                        timestamp: new Date().toLocaleTimeString(),
                        stage: 'error',
                        message: '❌ Request timed out'
                    });
                } else {
                    addLog({
                        timestamp: new Date().toLocaleTimeString(),
                        stage: 'error',
                        message: `❌ Connection failed: ${error.message}`
                    });
                }
                flushSync(() => {
                    setLoading(false);
                });
                message.error('Failed to start extraction');
                console.error('Error:', error);
            }
        }
    };

    // 下载完整数据表格
    const handleDownload = () => {
        if (fullData.length === 0) {
            message.warning('No data available to download');
            return;
        }

        // 生成CSV数据
        const headers = Object.keys(fullData[0]).filter(key => key !== 'id').join(',');
        const csvRows = fullData.map(row => {
            return Object.entries(row)
                .filter(([key]) => key !== 'id')
                .map(([_, value]) => {
                    // 处理内容中的逗号和引号，确保CSV格式正确
                    if (value === null || value === undefined) return '""';
                    const stringValue = String(value);
                    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return `"${stringValue}"`;
                }).join(',');
        });
        const csvContent = [headers, ...csvRows].join('\n');

        // 创建下载链接
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `extracted_data_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 停止提取
    const handleStopExtraction = async () => {
        // 先取消后端任务
        await cancelCurrentTask();
        
        // 关闭连接
        closeEventSource();
        
        addLog({
            timestamp: new Date().toLocaleTimeString(),
            stage: 'stopped',
            message: '⏹️ User stopped the extraction process'
        });
        flushSync(() => {
            setLoading(false);
        });
        message.info('Extraction stopped');
    };

    // 获取阶段显示名称
    const getStageDisplayName = (stage) => {
        const stageNames = {
            'init': 'Initialization',
            'parsing': 'PDF Parsing',
            'extraction': 'Information Extraction',
            'processing': 'Data Processing',
            'complete': 'Completed',
            'error': 'Error',
            'stopped': 'Stopped'
        };
        return stageNames[stage] || stage;
    };

    return (
        <div className="auto-data-page">
            <div className="auto-data-container">
                {/* Header Section */}
                <div className="auto-data-header">
                    <div className="auto-data-header-content">
                        <h1 className="auto-data-main-title">Auto Data Extraction</h1>
                        <p className="auto-data-subtitle">Extract structured alloy property data from research PDFs with AI-assisted parsing.</p>
                        <p style={{fontWeight: 500}}>
                            Remaining extractions: {quota !== null ? `${quota}/10` : 'Loading...'}
                        </p>
                    </div>
                </div>

                {/* Content Section */}
                <div className="auto-data-content">
                    <div className="upload-section">
                        <Upload
                            accept=".pdf"
                            beforeUpload={handleUpload}
                            showUploadList={false}
                            disabled={loading}
                        >
                            <Button icon={<UploadOutlined />} disabled={loading}>Upload PDF</Button>
                        </Upload>
                        <Button 
                            onClick={handleDefaultExample}
                            style={{ marginLeft: 16 }}
                            disabled={loading}
                        >
                            Load Default Example
                        </Button>
                    </div>
                    
                    {/* 文件名和预览 */}
                    {selectedFile && (
                        <div className="selected-file-preview">
                            <FilePdfOutlined style={{ color: '#d32029', fontSize: 20, marginRight: 8 }} />
                            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                                {isDefault ? DEFAULT_PDF_NAME : selectedFile.name}
                            </a>
                            <Space style={{ marginLeft: 16 }}>
                                <Button 
                                    type="primary" 
                                    onClick={handleExtractStream} 
                                    loading={loading}
                                    disabled={loading || !userId || quota === null}
                                >
                                    {loading ? 'Extracting...' : 'Extract'}
                                </Button>
                                {loading && (
                                    <Button 
                                        type="default" 
                                        onClick={handleStopExtraction}
                                        danger
                                    >
                                        Stop
                                    </Button>
                                )}
                                {data.length > 0 && (
                                    <Button 
                                        type="default" 
                                        icon={<DownloadOutlined />} 
                                        onClick={handleDownload}
                                        className="download-button"
                                        disabled={loading}
                                    >
                                        Download Full Data
                                    </Button>
                                )}
                                {logs.length > 0 && (
                                    <Button 
                                        type="default" 
                                        icon={<ClearOutlined />} 
                                        onClick={clearLogs}
                                        disabled={loading}
                                    >
                                        Clear Logs
                                    </Button>
                                )}
                            </Space>
                        </div>
                    )}

                    {/* 实时日志显示 */}
                    {(loading || logs.length > 0) && (
                        <div className="extraction-logs">
                            <div className="logs-header">
                                <h3>🔍 Extraction Progress</h3>
                                {currentStage && (
                                    <span className={`current-stage stage-${currentStage}`}>
                                        {getStageDisplayName(currentStage)}
                                    </span>
                                )}
                                {loading && logs.length === 0 && (
                                    <span className="current-stage stage-init">
                                        Connecting...
                                    </span>
                                )}
                            </div>
                            <div className="logs-container" ref={logsContainerRef}>
                                {logs.length === 0 && loading ? (
                                    <div className="log-entry stage-init">
                                        <span className="log-timestamp">{new Date().toLocaleTimeString()}</span>
                                        <span className="log-message">🔄 Establishing connection...</span>
                                    </div>
                                ) : (
                                    logs.map((log, index) => (
                                        <div key={log.id} className={`log-entry stage-${log.stage}`}>
                                            <span className="log-timestamp">{log.timestamp}</span>
                                            <span className="log-message">{log.message}</span>
                                            {process.env.NODE_ENV === 'development' && (
                                                <span style={{fontSize: '10px', color: '#999', marginLeft: '10px'}}>
                                                    #{index + 1}
                                                </span>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* 论文总结显示区域 */}
                    {paperSummary && (
                        <div className="paper-summary-section">
                            <h3>📄 Paper Summary</h3>
                            {paperMetadata.title && (
                                <div className="paper-metadata">
                                    <div className="metadata-item">
                                        <strong>Title:</strong> {paperMetadata.title}
                                    </div>
                                    {paperMetadata.journal && (
                                        <div className="metadata-item">
                                            <strong>Journal:</strong> {paperMetadata.journal}
                                        </div>
                                    )}
                                    {paperMetadata.publication_date && (
                                        <div className="metadata-item">
                                            <strong>Publication Date:</strong> {paperMetadata.publication_date}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="summary-content">
                                {paperSummary}
                            </div>
                        </div>
                    )}

                    <div className="auto-data-table-wrapper">
                        {loading ? (
                            <div className="loading-container">
                                <Spin size="large" />
                                <p>Extracting data from PDF... Check progress above!</p>
                            </div>
                        ) : (
                            <Table 
                                columns={columns} 
                                dataSource={data} 
                                rowKey="id"
                                scroll={{ x: true }}
                                pagination={{ pageSize: 10 }}
                                locale={{ emptyText: 'No data extracted yet. Upload a PDF and click Extract.' }}
                            />
                        )}
                    </div>
                    
                    {/* Help Section */}
                    <div className={`auto-data-help${isHelpOpen ? '' : ' help-collapsed'}`}>
                        <div className="help-header">
                            <h3>How to use Auto Data Extraction</h3>
                            <button
                                type="button"
                                className="help-toggle-button"
                                onClick={() => setIsHelpOpen(prev => !prev)}
                                aria-expanded={isHelpOpen}
                            >
                                {isHelpOpen ? 'Hide' : 'Show'}
                            </button>
                        </div>
                        {isHelpOpen && (
                            <div className="help-content">
                                <div className="help-step">
                                    <div className="step-number">1</div>
                                    <div className="step-content">
                                        <h4>Upload Document</h4>
                                        <p>Click "Upload PDF" to select a research paper, or use "Load Default Example" to try the built-in sample.</p>
                                    </div>
                                </div>
                                <div className="help-step">
                                    <div className="step-number">2</div>
                                    <div className="step-content">
                                        <h4>Start Extraction</h4>
                                        <p>Click "Extract" to analyze the document and extract alloy-related data with real-time progress tracking.</p>
                                    </div>
                                </div>
                                <div className="help-step">
                                    <div className="step-number">3</div>
                                    <div className="step-content">
                                        <h4>Review Results</h4>
                                        <p>View extracted properties in the table and download complete data as CSV for further analysis.</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
        </div>
    );
};

export default AutoDataPage; 
